import { AiStar, WatchLaterIcon } from '@/assets/icons/icons';
import ArrowBack from '@/assets/images/ArrowBack';
import { useToken } from '@/common/helper';
import { getPermissions } from '@/common/helper/getPermissions';
import { getFormattedTime } from '@/common/utils/getFormattedTimeUtils';
import { CustomButton } from '@/components';
import { CloseOutlined } from '@ant-design/icons';
import { Flex, Tooltip } from 'antd';
import React, { ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux-toolkit/store';
import EmailCallActions from '../EmailCallActions';
import AlertBox from './AlertBox';
import Pill from './Pill';
import styles from './AIAlertDetails.module.css';

interface Recipient {
  id: string;
  name: string;
  role?: string;
}

interface SentimentConfig {
  color: string;
  label: string;
}

interface HeaderProps {
  onClose: (closeAll: boolean) => void;
  subtitle: string;
  title: string;
  pillLabel?: string;
  showHeaderButtons?: boolean;
  /** The email panel's Figma spacing and sizes; the call panel leaves it off. */
  emailLayout?: boolean;
}

interface RecipientsDisplayProps {
  recipients?: Recipient[];
}

interface TriggeredByProps {
  triggeredBy?: string;
  recipient?: Recipient[];
  message?: string;
  emailLayout?: boolean;
}

interface MessageBlockProps {
  message: string;
}

interface SentimentBlockProps {
  sentimentLabel: string;
  emailLayout?: boolean;
}

interface NextStepProps {
  nextStep?: string;
  onDismiss?: () => void;
  onResolve?: () => void;
}

export interface ActivityItem {
  id: string;
  status: string;
  name: string;
  comment: string | null;
  title: string;
  summary: string;
  timestamp: string;
  comment_by: number;
  event: string;
  ai_type: string | null;
  length?: number | null;
}

interface FooterInfoProps {
  timestamp?: string;
  duration?: string;
  comments?: ActivityItem[];
  getFormattedTime?: void;
}

export interface AiEvent {
  id: string;
}

export interface Alert {
  is_critical?: boolean;
  id?: string;
  notification_type?: 'one_time_notification' | string;
  ai_events?: AiEvent[];
}

interface AIAlertDetailsProps {
  isCritical: boolean;
  alert: Alert;
  showNextStep?: boolean;
  subtitle: string;
  title: string;
  alertTitle: string;
  pillLabel?: string;
  triggeredBy?: string;
  recipient?: Recipient[];
  message?: string;
  sentimentLabel?: string;
  summary?: string;
  agentId?: string | null;
  agentName?: string | null;
  agentPurpose?: string | null;
  onOpenAgent?: (agentId: string, readOnly: boolean) => void;
  nextStep?: string;
  onDismiss?: () => void;
  onResolve?: () => void;
  onClose: (closeAll: boolean) => void;
  bound_caller?: string;
  resolutionWindow?: React.ReactNode;
  showHeaderButtons?: boolean;
  /**
   * The email panel's look from the Figma: 4 px sparkle gap, 16 px close icon,
   * 20 px "Resolved" tag, grey "Triggered by" line, 24 px alert icon, and one
   * sentiment line plus "AI summary:" paragraph (no Sentiment / Agent
   * headings). Off by default, so the call panel looks as it did.
   */
  emailLayout?: boolean;
  /** The bold lead-in of the email layout's summary ("AI summary:"); shown only when given. */
  summaryLabel?: string;
}

/**
 * Header component for AI alert details with navigation controls
 *
 * @param onClose - Callback function that receives boolean indicating if close all should be triggered
 * @param subtitle - Subtitle text to display
 * @param title - Main title text to display
 * @param pillLabel - Optional pill label to display next to title
 */
export const Header: React.FC<HeaderProps> = ({
  onClose,
  subtitle,
  title,
  pillLabel,
  showHeaderButtons,
  emailLayout,
}) => (
  <div className={`${styles.header} ${emailLayout ? styles.headerEmail : ''}`}>
    {showHeaderButtons && (
      <Flex align='center' justify='space-between' style={{ width: '100%' }}>
        <ArrowBack
          data-testid='transcript-back'
          fill='var(--text-primary)'
          style={{ width: 16, height: 16, cursor: 'pointer' }}
          onClick={() => onClose(false)}
        />
        <CloseOutlined
          data-testid='transcript-close'
          style={
            emailLayout
              ? { fontSize: 16, cursor: 'pointer' }
              : { width: 13, height: 13, paddingTop: 5, cursor: 'pointer' }
          }
          onClick={() => onClose(true)}
        />
      </Flex>
    )}

    <Flex align='center' gap={emailLayout ? 4 : 8}>
      <AiStar color='#3D81FF' />
      <div className='ai-header-subtitle'>{subtitle}</div>
    </Flex>

    <Flex align='center' gap={8}>
      <div className='ai-header-title'>{title}</div>
      {pillLabel && (
        <Pill className={emailLayout ? styles.emailPill : undefined}>
          {pillLabel}
        </Pill>
      )}
    </Flex>
  </div>
);

const RecipientsDisplay: React.FC<RecipientsDisplayProps> = ({
  recipients = [],
}) => {
  if (recipients.length === 0) return null;

  const formatRecipient = (r: Recipient): ReactNode => (
    <>
      {r.name}
      {r.role ? ` (${r.role})` : ''}
    </>
  );

  if (recipients.length === 1) {
    return <span>{formatRecipient(recipients[0])}</span>;
  }

  const [first, ...rest] = recipients;
  return (
    <span>
      {formatRecipient(first)}
      <Tooltip
        title={rest.map((r) => (
          <div key={r.id}>{formatRecipient(r)}</div>
        ))}
      >
        <span className='cursor-pointer'>+{rest.length}</span>
      </Tooltip>
    </span>
  );
};

/**
 * Component displaying who triggered the alert and recipients
 *
 * @param triggeredBy - Name of the person who triggered the alert
 * @param recipient - Array of recipient objects
 * @param message - Optional message content
 */
export const TriggeredBy: React.FC<TriggeredByProps> = ({
  triggeredBy,
  recipient,
  message,
  emailLayout,
}) => (
  <div
    className={`${emailLayout ? styles.emailTriggered : 'ai-triggered'} ${
      message ? styles.withMessage : ''
    }`}
  >
    {triggeredBy && (
      <span>
        Triggered by <span>{triggeredBy}</span>
      </span>
    )}

    {recipient && (
      <>
        {' '}
        → <RecipientsDisplay recipients={recipient} />
      </>
    )}
  </div>
);

/**
 * Component for displaying alert message content
 *
 * @param message - The message content to display
 */
export const MessageBlock: React.FC<MessageBlockProps> = ({ message }) => (
  <div className='ai-message'>
    <span className={styles.messageText}>Message: {message}</span>
  </div>
);

const sentimentConfig: Record<string, SentimentConfig> = {
  positive: { color: 'var(--green-900)', label: 'Positive sentiment' },
  negative: { color: 'var(--danger)', label: 'Negative sentiment' },
  neutral: { color: 'var(--light-blue)', label: 'Neutral sentiment' },
  awaiting_reply: { color: 'var(--text-secondary)', label: 'Awaiting reply' },
};

/**
 * Component for displaying sentiment analysis and AI summary
 *
 * @param sentimentLabel - The sentiment classification (positive, negative, neutral, etc.)
 * @param summary - AI-generated summary of the interaction
 */
export const SentimentBlock: React.FC<SentimentBlockProps> = ({
  sentimentLabel,
  emailLayout,
}) => {
  const key = sentimentLabel?.toLowerCase();
  const { color, label } = sentimentConfig[key] || {
    label: sentimentLabel,
  };

  return (
    <div className='ai-sentiment'>
      <Flex align='center' gap={8}>
        {key === 'awaiting_reply' ? (
          <WatchLaterIcon color={color} width={10} height={10} />
        ) : (
          <span
            className={`${styles.dot} ${emailLayout ? styles.dotEmail : ''}`}
            // The colour comes from the sentiment at run time.
            // eslint-disable-next-line react/forbid-dom-props
            style={{ backgroundColor: color }}
          />
        )}
        <span
          className={`${styles.sentimentLabel} ${
            emailLayout ? styles.sentimentLabelEmail : ''
          }`}
        >
          {label}
        </span>
      </Flex>
    </div>
  );
};

/**
 * Component for displaying next steps and action buttons
 *
 * @param nextStep - Description of the next recommended action
 * @param onDismiss - Callback function for dismiss action
 * @param onResolve - Callback function for resolve action
 */
export const NextStep: React.FC<NextStepProps> = ({
  nextStep,
  onDismiss,
  onResolve,
}) => (
  <div className='ai-nextstep'>
    <Flex align='center' gap={8} className='mb-2'>
      <span className='font-semibold text-xs'>Next step:</span>
      <span className='text-xs'>{nextStep}</span>
    </Flex>
    <Flex gap={12}>
      <CustomButton
        fontSize='12px'
        fontWeight='600'
        text='Dismiss'
        bgColor='var(--gray-300)'
        color='var(--text-primary)'
        height='28px'
        width='236px'
        boxShadow='none'
        onClick={onDismiss}
      />
      <CustomButton
        fontSize='12px'
        fontWeight='600'
        text='Mark as resolved'
        bgColor='var(--gray-300)'
        color='var(--text-primary)'
        height='28px'
        width='236px'
        boxShadow='none'
        onClick={onResolve}
      />
    </Flex>
  </div>
);

/**
 * Main AIAlertDetails component for displaying comprehensive alert information
 *
 * @param showNextStep - Whether to display the next steps section
 * @param subtitle - Subtitle for the alert header
 * @param title - Main title for the alert
 * @param pillLabel - Optional status pill label
 * @param triggeredBy - Name of person who triggered the alert
 * @param recipient - Array of alert recipients
 * @param message - Alert message content
 * @param sentimentLabel - Sentiment classification
 * @param summary - AI-generated summary
 * @param nextStep - Next recommended action
 * @param onDismiss - Callback for dismiss action
 * @param onResolve - Callback for resolve action
 * @param onClose - Callback for close action, receives boolean for close all
 */
const AIAlertDetails: React.FC<AIAlertDetailsProps> = ({
  alert,
  isCritical,
  showNextStep,
  subtitle,
  title,
  alertTitle,
  pillLabel,
  triggeredBy,
  recipient,
  message,
  sentimentLabel,
  summary,
  agentId,
  agentName,
  agentPurpose,
  onOpenAgent,
  nextStep,
  onDismiss,
  onResolve,
  onClose,
  bound_caller,
  showHeaderButtons,
  resolutionWindow,
  emailLayout,
  summaryLabel,
}) => {
  const main_menu = useSelector((state: RootState) => state.menu.items);
  const agentsPermissions = getPermissions(main_menu, 'Settings', 'AI agents');
  const updatedTitle =
    title?.replace(/\b(Alerts?)\s*$/i, (match) => match.toLowerCase()) ?? '';
  const updatedAlertLabel = alertTitle?.replace(/\b(Alerts?)\s*$/i, '') ?? '';

  return (
    <>
      <Header
        onClose={onClose}
        subtitle={subtitle}
        title={updatedTitle}
        pillLabel={pillLabel}
        showHeaderButtons={showHeaderButtons}
        emailLayout={emailLayout}
      />
      {bound_caller ? (
        <p className='ai-triggered'>{bound_caller}</p>
      ) : (
        <TriggeredBy
          triggeredBy={triggeredBy}
          recipient={recipient}
          message={message}
          emailLayout={emailLayout}
        />
      )}
      {alert?.notification_type === 'one_time_notification' ? (
        message && <MessageBlock message={message} />
      ) : (
        <AlertBox
          message={message}
          label={updatedAlertLabel}
          isCritical={isCritical}
          emailLayout={emailLayout}
        />
      )}

      {/* Resolution Window - positioned right after the alert box */}
      {resolutionWindow}

      {emailLayout ? (
        <div className={styles.emailSummaryBlock}>
          {sentimentLabel && (
            <SentimentBlock sentimentLabel={sentimentLabel} emailLayout />
          )}
          {summary && (
            <p className={styles.emailSummary}>
              {summaryLabel && (
                <>
                  <strong>{summaryLabel}</strong>{' '}
                </>
              )}
              {summary}
            </p>
          )}
        </div>
      ) : (
        <>
          <Flex align='flex-start' gap={160}>
            {sentimentLabel && (
              <div>
                <div className='ai-sentiment-label'>Sentiment</div>
                <SentimentBlock sentimentLabel={sentimentLabel} />
              </div>
            )}
            {agentName && (
              <div className={styles.agentColumn}>
                <div className='ai-sentiment-label'>Agent</div>
                {agentsPermissions?.is_view && agentId ? (
                  <Tooltip
                    title={`${agentName}${agentPurpose ? ` (${agentPurpose})` : ''}`}
                    placement='top'
                  >
                    <div
                      role='button'
                      tabIndex={0}
                      className={styles.agentLink}
                      onClick={() =>
                        onOpenAgent?.(agentId, !agentsPermissions?.is_update)
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ')
                          onOpenAgent?.(agentId, !agentsPermissions?.is_update);
                      }}
                    >
                      {agentName}
                      {agentPurpose ? ` (${agentPurpose})` : ''}
                    </div>
                  </Tooltip>
                ) : (
                  <Tooltip
                    title={`${agentName}${agentPurpose ? ` (${agentPurpose})` : ''}`}
                    placement='top'
                  >
                    <div className={styles.agentText}>
                      {agentName}
                      {agentPurpose ? ` (${agentPurpose})` : ''}
                    </div>
                  </Tooltip>
                )}
              </div>
            )}
          </Flex>
          {summary && (
            <div className={styles.summaryBlock}>
              <div className='ai-sentiment-label'>AI summary</div>
              <div className='ai-summary'>{summary}</div>
            </div>
          )}
        </>
      )}
      {showNextStep && (
        <NextStep
          nextStep={nextStep}
          onDismiss={onDismiss}
          onResolve={onResolve}
        />
      )}
    </>
  );
};

export default AIAlertDetails;

/**
 * Footer component displaying timestamp and duration information
 *
 * @param timestamp - ISO timestamp string
 * @param duration - Duration string (e.g., "5 minutes")
 */

export const FooterInfo: React.FC<FooterInfoProps> = ({
  timestamp,
  duration,
  comments,
}) => {
  const { decodedUserToken } = useToken();
  const isTimeZone = decodedUserToken()?.time_zone_enable;
  const dateFormat = decodedUserToken()?.date_format;

  const [showFull, setShowFull] = useState<boolean>(false);

  return (
    <>
      <Flex justify='space-between'>
        <span
          className='ai-footer cursor-pointer'
          onClick={() => setShowFull((prev) => !prev)}
        >
          {timestamp
            ? !showFull
              ? getFormattedTime(timestamp, isTimeZone, dateFormat)?.full
              : getFormattedTime(timestamp, isTimeZone, dateFormat)?.relative
            : 'Start time not available'}
        </span>
        <span className='ai-footer'>Duration: {duration || 'N/A'}</span>
      </Flex>

      {(comments?.length ?? 0) > 0 && (
        <EmailCallActions alertActions={comments} />
      )}
    </>
  );
};
